/* Copyright (C) 2023 Advenio Tecnosys Pvt. Ltd.

*        All Rights Reserved

*

 * NOTICE: 

 * All information contained herein is, and remains

* the property of Advenio Tecnosys and its suppliers,

* if any.  The intellectual and technical concepts contained

* herein are proprietary to Advenio Tecnosys

* and its suppliers and may be covered by Indian, U.S. and Foreign Patents,

* patents in process, and are protected by trade secret or copyright law.

* Appropriation, redistribution, dissemination of this information or reproduction

* of this material is strictly forbidden unless prior written permission is obtained

* from Advenio Tecnosys Pvt. Ltd.

*

* Author: < codebhendi, Ravi Ranjan Kumar, Sanket, Mylavarapu Sarath Sai, Amal Sabu, Ujjwal Singh >

*/
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import clsx from "clsx";
import { makeStyles, useTheme } from "@material-ui/styles";
import { useMediaQuery } from "@material-ui/core";
import { useCookies } from "react-cookie";
import { withRouter } from "react-router";

import { Sidebar, Topbar, Footer } from "./components";
import LogOutModel from "components/AlertPopUp/alert-pop-up";
import HelpMenuModel from "components/AlertPopUp/help-menu-pop-up"
import SnackBar from "../../components/SnackBar/snackbar";
// common util
// import { userAgentDetection } from "common/common-util-functions";
import { useIdleTimer } from "react-idle-timer";
import { removeItemsFromSessionStorageToLogout } from "helpers/common";
import { removeAuthenticationFromSessionStorage } from "helpers/auth-session";
import { clearSession as clearS3UploadSession } from "helpers/s3UploadSession";

// action
import {
 getNetSpeed,
 updateUserAgent,
 updateLogOutModel,
 updateHelpMenuModel,
} from "actions/global-action";

const useStyles = makeStyles((theme) => ({
 root: {
  paddingTop: 56,
  height: "100%",
  overflow: "auto",
  backgroundColor: theme.palette.primary.main,
  [theme.breakpoints.up("sm")]: {
   paddingTop: 64,
  },
 },
 drawer: {
  width: 240,
  [theme.breakpoints.up("sm")]: {
   marginTop: 64,
   height: "calc(100% - 64px)",
  },
 },
 drawerMini: {
  width: 70,
  [theme.breakpoints.up("sm")]: {
   marginTop: 64,
   height: "calc(100% - 64px)",
  },
 },
 shiftContent: {
  paddingLeft: 240,
 },
 shiftContentMini: {
  paddingLeft: 70,
 },
 content: {
  // height: '100%',
  // backgroundColor:palette.primary.main
 },
}));

const Main = (props) => {
 const { children } = props;
 const classes = useStyles();
 const theme = useTheme();
 const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
  defaultMatches: true,
 });
 const [openSidebar, setOpenSidebar] = useState(false);
 const [sidebarMini, setSidebarMini] = useState(false);
 const [logoutAlert, setLogoutAlert] = useState(false);
 const timeout = 1800000;
 const [remaining, setRemaining] = useState(timeout);
 const [lastActive, setLastActive] = useState(new Date().getTime());
 const [isIdle, setIsIdle] = useState(false);
 const [cookies, setCookie] = useCookies([]);

 React.useEffect(() => {
  var speedData = navigator.connection;
  props.getNetSpeed(speedData);
 }, [props.globalStore.speedData]);
 const handleSidebarOpen = () => {
  setOpenSidebar(true);
 };
 const handleSidebarMini = () => {
  setSidebarMini(!sidebarMini);
 };
 const handleSidebarClose = () => {
  setOpenSidebar(false);
 };
 const handleCloseModel = () => {
  props.updateLogOutModel(false);
  props.updateHelpMenuModel(false);
 };
  const handleSaveChanges = () => {
   clearS3UploadSession();
   removeAuthenticationFromSessionStorage();
   removeItemsFromSessionStorageToLogout();
  setCookie(
   "authentication",
   { user_id: "", authToken: "" },
   { path: "/" },
   "high"
  );
  window.location.replace("/sign-in");
 };
 const shouldOpenSidebar = isDesktop ? true : openSidebar;

 const handleOnActive = () => setIsIdle(false);
 const handleOnIdle = () => setIsIdle(true);

 const { getRemainingTime, getLastActiveTime } = useIdleTimer({
  timeout,
  onActive: handleOnActive,
  onIdle: handleOnIdle,
 });

 useEffect(() => {
  setRemaining(getRemainingTime());
  setLastActive(getLastActiveTime());

  const interval = setInterval(() => {
   setLastActive(getLastActiveTime());
   setRemaining(getRemainingTime());
  }, 1000);

  return () => clearInterval(interval);
 }, []);

 useEffect(() => {
  if (remaining === 0) {
   handleSaveChanges();
  } else if (remaining > 301000) {
   logoutAlert && handleCloseSnackbar();
   return;
  } else if (remaining < 300000) {
   !logoutAlert && setLogoutAlert(true);
  }
 }, [remaining]);

 const handleCloseSnackbar = () => {
  setLogoutAlert(false);
 };

 return (
  <div
   className={clsx({
    [classes.root]: true,
    [sidebarMini ? classes.shiftContentMini : classes.shiftContent]: isDesktop,
   })}
  >
   <Topbar
    onSidebarOpen={handleSidebarOpen}
    onSidebarMini={handleSidebarMini}
   />
   {logoutAlert && (
    <SnackBar
     open={true}
     onClose={handleCloseSnackbar}
     message={"You are about to be logged out in 5 minutes due to inactivity."}
     variant={"error"}
     duration={299000}
    />
   )}
   <Sidebar
    onClose={handleSidebarClose}
    open={shouldOpenSidebar}
    variant={isDesktop ? "persistent" : "temporary"}
    sidebarMini={sidebarMini}
   />
   <main className={classes.content} id="main-content">
    {children}
    {/* <Footer /> */}

    <LogOutModel
     isOpenModel={props.globalStore.isLogOutPopUpModel}
     handleCloseModel={handleCloseModel}
     handleSaveChanges={handleSaveChanges}
     msg={"Do you wish to exit the application?"}
     title={"LOGOUT CONFIRMATION!"}
    />
    <HelpMenuModel
    isOpenModel={props.globalStore.isHelpMenuPopUpModel}
    handleCloseModel={handleCloseModel}
    title={"Software Updates !"}
    helpMenuData={props.globalStore.versionInfo}
    />
   </main>
  </div>
 );
};

Main.propTypes = {
 children: PropTypes.node,
};
function mapStateToProps(store) {
 return {
  globalStore: store.globalStore,
  patientStore: store.patientStore,
 };
}
export default connect(mapStateToProps, {
 getNetSpeed,
 updateUserAgent,
 updateLogOutModel,
 updateHelpMenuModel,
})(withRouter(Main));
